/**
 * FrameworkGroup Component
 * Groups frictions by regulatory framework, showing entity and relationship frictions together
 * Requirements: 18.1, 18.2, 18.3, 18.4
 */

import React, { useState } from 'react';
import { ExpandableList, ExpandableListItem } from './ExpandableList';

export interface FrictionItem {
  id: string;
  name: string;
  frictionType: 'entity_friction' | 'relationship_friction' | 'regulatory_friction';
  direction: 'positive' | 'negative';
  notes?: string;
  magnitude?: number;
  baselineTension?: number;
  multiplier?: number;
  logicFormula?: string;
  conditions?: string;
  regulatoryFramework?: string;
  customAttributes?: {
    regulatoryFramework?: string;
    [key: string]: any;
  };
}

export interface FrameworkGroupProps {
  frameworkName: string;
  frameworkDisplayName: string;
  frictions: FrictionItem[];
  affectedItemsCache: Map<string, { entities?: ExpandableListItem[], relationships?: ExpandableListItem[], count: number }>;
  loadingAffectedItems: Set<string>;
  onEditFriction: (friction: FrictionItem) => void;
  onCopyFriction: (friction: FrictionItem) => void;
  onRemoveFriction: (frictionId: string) => void;
  onNavigateToEntity: (entityId: string) => void;
  onNavigateToRelationship: (relationshipId: string) => void;
  onFetchAffectedEntities: (frictionId: string) => void;
  onFetchAffectedRelationships: (frictionId: string) => void;
  onAddFriction?: () => void; // Optional callback for "Add Friction" button
}

export const FrameworkGroup: React.FC<FrameworkGroupProps> = ({
  frameworkName,
  frameworkDisplayName,
  frictions,
  affectedItemsCache,
  loadingAffectedItems,
  onEditFriction,
  onCopyFriction,
  onRemoveFriction,
  onNavigateToEntity,
  onNavigateToRelationship,
  onFetchAffectedEntities,
  onFetchAffectedRelationships,
  onAddFriction
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

  // Count entity and relationship frictions
  const entityFrictionCount = frictions.filter(f => f.frictionType === 'entity_friction' || f.frictionType === 'regulatory_friction').length;
  const relationshipFrictionCount = frictions.filter(f => f.frictionType === 'relationship_friction').length;
  const totalCount = frictions.length;

  return (
    <div className="framework-group" style={{
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      marginBottom: '16px',
      background: '#ffffff'
    }}>
      {/* Framework Header */}
      <div
        className="framework-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          background: '#f9fafb',
          borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
          borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
          cursor: 'pointer'
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', color: '#6b7280' }}>
            {isExpanded ? '▼' : '▶'}
          </span>
          <div>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
              {frameworkDisplayName}
            </h4>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {totalCount} friction{totalCount !== 1 ? 's' : ''} 
              {entityFrictionCount > 0 && ` • ${entityFrictionCount} entity`}
              {relationshipFrictionCount > 0 && ` • ${relationshipFrictionCount} relationship`}
            </div>
          </div>
        </div>
        {onAddFriction && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddFriction();
            }}
            style={{
              padding: '6px 12px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            + Add Friction
          </button>
        )}
      </div>

      {/* Framework Content */}
      {isExpanded && (
        <div className="framework-content" style={{ padding: '16px' }}>
          {frictions.length === 0 ? (
            <div style={{
              padding: '24px',
              textAlign: 'center',
              color: '#6b7280',
              fontStyle: 'italic'
            }}>
              No frictions defined for this framework
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {frictions.map(friction => (
                <div
                  key={friction.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '12px',
                    background: '#fafafa'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      {/* Friction Name with Type Badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>
                          {friction.name}
                        </span>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: friction.frictionType === 'relationship_friction' ? '#dbeafe' : '#fef3c7',
                          color: friction.frictionType === 'relationship_friction' ? '#1e40af' : '#92400e'
                        }}>
                          {friction.frictionType === 'relationship_friction' ? 'Relationship' : 'Entity'}
                        </span>
                      </div>

                      {/* Friction Details */}
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                        Direction: {friction.direction === 'negative' ? 'Negative (increases tension)' : 'Positive (decreases tension)'}
                      </div>

                      {/* Show appropriate values based on friction type */}
                      {friction.frictionType === 'relationship_friction' && friction.magnitude !== undefined && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                          Magnitude: {friction.magnitude.toFixed(1)}
                        </div>
                      )}

                      {(friction.frictionType === 'entity_friction' || friction.frictionType === 'regulatory_friction') && (
                        <>
                          {friction.baselineTension !== undefined && friction.multiplier !== undefined ? (
                            <>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                Baseline Tension: {friction.baselineTension.toFixed(1)} | Multiplier: {friction.multiplier.toFixed(1)}x
                              </div>
                              <div style={{ fontSize: '11px', color: '#3b82f6', fontStyle: 'italic', marginBottom: '4px' }}>
                                {Math.abs(friction.multiplier - (1.0 + friction.baselineTension / 10)) < 0.01 
                                  ? `📊 Simple Mode (Magnitude: ${friction.baselineTension.toFixed(1)})`
                                  : '⚙️ Advanced Mode'}
                              </div>
                            </>
                          ) : friction.magnitude !== undefined && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                              Magnitude: {friction.magnitude.toFixed(1)} (Legacy format)
                            </div>
                          )}
                        </>
                      )}

                      {/* Logic Formula */}
                      {(friction.logicFormula || friction.conditions) && (
                        <div style={{
                          fontSize: '12px',
                          color: '#4b5563',
                          fontFamily: 'monospace',
                          marginTop: '6px',
                          background: '#f3f4f6',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          wordBreak: 'break-all'
                        }}>
                          {friction.conditions || friction.logicFormula}
                        </div>
                      )}

                      {/* Notes - Expandable */}
                      {friction.notes && (
                        <div style={{ marginTop: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const newExpanded = new Set(expandedNotes);
                              if (newExpanded.has(friction.id)) {
                                newExpanded.delete(friction.id);
                              } else {
                                newExpanded.add(friction.id);
                              }
                              setExpandedNotes(newExpanded);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#3b82f6',
                              cursor: 'pointer',
                              fontSize: '12px',
                              padding: '0',
                              textDecoration: 'underline',
                              fontWeight: '500'
                            }}
                          >
                            {expandedNotes.has(friction.id) ? '▼ Hide Notes' : '▶ Show Notes'}
                          </button>
                          {expandedNotes.has(friction.id) && (
                            <div style={{
                              fontSize: '12px',
                              color: '#4b5563',
                              marginTop: '6px',
                              padding: '8px',
                              background: '#fffbeb',
                              border: '1px solid #fcd34d',
                              borderRadius: '4px',
                              lineHeight: '1.5'
                            }}>
                              {friction.notes}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Expandable List for Affected Items */}
                      <ExpandableList
                        frictionId={friction.id}
                        frictionType={friction.frictionType}
                        items={
                          friction.frictionType === 'relationship_friction'
                            ? affectedItemsCache.get(friction.id)?.relationships || []
                            : affectedItemsCache.get(friction.id)?.entities || []
                        }
                        count={affectedItemsCache.get(friction.id)?.count || 0}
                        isLoading={loadingAffectedItems.has(friction.id)}
                        onItemClick={
                          friction.frictionType === 'relationship_friction'
                            ? onNavigateToRelationship
                            : onNavigateToEntity
                        }
                        onExpand={() => {
                          if (friction.frictionType === 'relationship_friction') {
                            onFetchAffectedRelationships(friction.id);
                          } else {
                            onFetchAffectedEntities(friction.id);
                          }
                        }}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '6px', marginLeft: '12px' }}>
                      <button
                        onClick={() => onEditFriction(friction)}
                        style={{
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onCopyFriction(friction)}
                        style={{
                          padding: '6px 12px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => onRemoveFriction(friction.id)}
                        style={{
                          padding: '6px 12px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
